import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";
import type { BookingProposalDto } from "../types/conversations";

type LoadState = "loading" | "ready" | "error";

type SectionKey = "upcoming" | "paid" | "completed" | "cancelled";

const WalkerBookingsPage: React.FC = () => {
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>("loading");
  const [items, setItems] = useState<BookingProposalDto[]>([]);
  const [error, setError] = useState("");
  const [busyProposalId, setBusyProposalId] = useState<number | null>(null);

  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    upcoming: true,
    paid: false,
    completed: false,
    cancelled: false,
  });

  useEffect(() => {
    async function load() {
      try {
        const bookings = await walkiesClient.getWalkerBookings();
        setItems(bookings);
        setState("ready");
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Unable to load bookings."
        );
        setState("error");
      }
    }

    void load();
  }, []);

  const grouped = useMemo(() => {
    const todayKey = toDateKey(new Date());

    const sorted = [...items].sort((a, b) => {
      const aDate = a.serviceDateLocal || "";
      const bDate = b.serviceDateLocal || "";

      if (aDate !== bDate) {
        return aDate.localeCompare(bDate);
      }

      const aTime = a.startTimeLocal || "";
      const bTime = b.startTimeLocal || "";
      if (aTime !== bTime) {
        return aTime.localeCompare(bTime);
      }

      return b.bookingProposalId - a.bookingProposalId;
    });

    const upcoming: BookingProposalDto[] = [];
    const paid: BookingProposalDto[] = [];
    const completed: BookingProposalDto[] = [];
    const cancelled: BookingProposalDto[] = [];

    for (const item of sorted) {
      const status = (item.status || "").trim().toLowerCase();
      const dateKey = item.serviceDateLocal || "";
      const isTodayOrFuture = !dateKey || dateKey >= todayKey;

      if (status === "completed") {
        completed.push(item);
        continue;
      }

      if (status === "cancelled") {
        cancelled.push(item);
        continue;
      }

      if (
        isTodayOrFuture &&
        (status === "paid" ||
          status === "sent" ||
          status === "checkoutcreated")
      ) {
        upcoming.push(item);
        continue;
      }

      if (
        status === "paid" ||
        status === "sent" ||
        status === "checkoutcreated"
      ) {
        paid.push(item);
        continue;
      }
    }

    return { upcoming, paid, completed, cancelled };
  }, [items]);

  async function reload() {
    const bookings = await walkiesClient.getWalkerBookings();
    setItems(bookings);
  }

  async function handleMarkCompleted(bookingProposalId: number) {
    try {
      setBusyProposalId(bookingProposalId);
      setError("");
      await walkiesClient.completeBookingProposal(bookingProposalId);
      await reload();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to mark booking as completed."
      );
    } finally {
      setBusyProposalId(null);
    }
  }

  async function handleCancel(bookingProposalId: number) {
    const confirmed = window.confirm(
      "Cancel this booking proposal? This will move it to cancelled bookings."
    );

    if (!confirmed) return;

    try {
      setBusyProposalId(bookingProposalId);
      setError("");
      await walkiesClient.cancelBookingProposal(bookingProposalId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to cancel booking.");
    } finally {
      setBusyProposalId(null);
    }
  }

  function toggleSection(key: SectionKey) {
    setOpenSections((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }

  if (state === "loading") {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.heroCard}>
            <h1 style={styles.title}>Loading bookings…</h1>
          </div>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.heroCard}>
            <h1 style={styles.title}>Unable to load bookings</h1>
            <p style={styles.textMuted}>{error || "Please sign in again."}</p>
            <div style={styles.actions}>
              <button
                style={styles.primaryButton}
                onClick={() => navigate("/walker/dashboard")}
              >
                Back to dashboard
              </button>
              <button
                style={styles.secondaryButton}
                onClick={() => navigate("/walker/access")}
              >
                Go to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalCount =
    grouped.upcoming.length +
    grouped.paid.length +
    grouped.completed.length +
    grouped.cancelled.length;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.heroCard}>
          <div style={styles.topRow}>
            <div>
              <div style={styles.badge}>Walker bookings</div>
              <h1 style={styles.title}>Your bookings</h1>
              <p style={styles.textMuted}>
                Manage live work first, then expand older bookings only when you
                need them.
              </p>
            </div>

            <div style={styles.topActions}>
              <button
                style={styles.secondaryButton}
                onClick={() => navigate("/walker/messages")}
              >
                View messages
              </button>
              <button
                style={styles.primaryButton}
                onClick={() => navigate("/walker/dashboard")}
              >
                Back to dashboard
              </button>
            </div>
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Upcoming</div>
              <div style={styles.summaryValue}>{grouped.upcoming.length}</div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Paid</div>
              <div style={styles.summaryValue}>{grouped.paid.length}</div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Completed</div>
              <div style={styles.summaryValue}>{grouped.completed.length}</div>
            </div>

            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Cancelled</div>
              <div style={styles.summaryValue}>{grouped.cancelled.length}</div>
            </div>
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          {totalCount === 0 ? (
            <div style={styles.emptyState}>
              <h2 style={styles.sectionTitle}>No bookings yet</h2>
              <p style={styles.textMuted}>
                Once you send booking proposals from conversations, they will
                appear here.
              </p>
            </div>
          ) : (
            <>
              <BookingSection
                sectionKey="upcoming"
                title="Upcoming bookings"
                emptyText="No upcoming bookings right now."
                items={grouped.upcoming}
                busyProposalId={busyProposalId}
                onComplete={handleMarkCompleted}
                onCancel={handleCancel}
                isOpen={openSections.upcoming}
                onToggle={toggleSection}
              />

              <BookingSection
                sectionKey="paid"
                title="Paid bookings"
                emptyText="No paid bookings outside the upcoming window."
                items={grouped.paid}
                busyProposalId={busyProposalId}
                onComplete={handleMarkCompleted}
                onCancel={handleCancel}
                isOpen={openSections.paid}
                onToggle={toggleSection}
              />

              <BookingSection
                sectionKey="completed"
                title="Completed bookings"
                emptyText="No completed bookings yet."
                items={grouped.completed}
                busyProposalId={busyProposalId}
                onComplete={handleMarkCompleted}
                onCancel={handleCancel}
                readOnly
                isOpen={openSections.completed}
                onToggle={toggleSection}
              />

              <BookingSection
                sectionKey="cancelled"
                title="Cancelled bookings"
                emptyText="No cancelled bookings."
                items={grouped.cancelled}
                busyProposalId={busyProposalId}
                onComplete={handleMarkCompleted}
                onCancel={handleCancel}
                readOnly
                isOpen={openSections.cancelled}
                onToggle={toggleSection}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

type BookingSectionProps = {
  sectionKey: SectionKey;
  title: string;
  emptyText: string;
  items: BookingProposalDto[];
  busyProposalId: number | null;
  onComplete: (bookingProposalId: number) => Promise<void>;
  onCancel: (bookingProposalId: number) => Promise<void>;
  isOpen: boolean;
  onToggle: (key: SectionKey) => void;
  readOnly?: boolean;
};

const BookingSection: React.FC<BookingSectionProps> = ({
  sectionKey,
  title,
  emptyText,
  items,
  busyProposalId,
  onComplete,
  onCancel,
  isOpen,
  onToggle,
  readOnly = false,
}) => {
  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!isOpen) return;

    if (items.length === 1) {
      setExpandedIds((current) => ({
        ...current,
        [items[0].bookingProposalId]: true,
      }));
    }
  }, [isOpen, items]);

  function toggleBooking(bookingProposalId: number) {
    setExpandedIds((current) => ({
      ...current,
      [bookingProposalId]: !current[bookingProposalId],
    }));
  }

  const latestItem = items.length > 0 ? items[0] : null;
  const previewText = latestItem
    ? `${formatDate(latestItem.serviceDateLocal)} · ${formatTimeRange(
        latestItem.startTimeLocal,
        latestItem.endTimeLocal
      )}`
    : emptyText;

  return (
    <section style={styles.section}>
      <button
        type="button"
        style={styles.sectionToggle}
        onClick={() => onToggle(sectionKey)}
      >
        <div style={styles.sectionToggleLeft}>
          <h2 style={styles.sectionTitle}>{title}</h2>
          <div style={styles.sectionPreview}>{previewText}</div>
        </div>

        <div style={styles.sectionToggleRight}>
          <div style={styles.sectionCount}>{items.length}</div>
          <div style={styles.chevron}>{isOpen ? "−" : "+"}</div>
        </div>
      </button>

      {!isOpen ? null : items.length === 0 ? (
        <div style={styles.emptyRow}>{emptyText}</div>
      ) : (
        <div style={styles.list}>
          {items.map((item) => {
            const normalizedStatus = (item.status || "").trim().toLowerCase();
            const isBusy = busyProposalId === item.bookingProposalId;
            const isExpanded = !!expandedIds[item.bookingProposalId];

            const canComplete = !readOnly && normalizedStatus === "paid";
            const canCancel =
              !readOnly &&
              (normalizedStatus === "sent" ||
                normalizedStatus === "checkoutcreated");

            return (
              <div key={item.bookingProposalId} style={styles.bookingCard}>
                <button
                  type="button"
                  style={styles.bookingSummaryButton}
                  onClick={() => toggleBooking(item.bookingProposalId)}
                >
                  <div style={styles.bookingSummaryMain}>
                    <div style={styles.bookingTitleRow}>
                      <div style={styles.bookingTitle}>
                        {item.serviceType || "Booking proposal"}
                      </div>

                      <div
                        style={{
                          ...styles.statusBadge,
                          ...getStatusBadgeStyle(item.status),
                        }}
                      >
                        {formatStatus(item.status)}
                      </div>
                    </div>

                    <div style={styles.bookingSummaryLine}>
                      {formatDate(item.serviceDateLocal)} ·{" "}
                      {formatTimeRange(item.startTimeLocal, item.endTimeLocal)}
                    </div>

                    <div style={styles.bookingSummaryLineSoft}>
                      Owner: {item.ownerEmail || "Unknown owner"}
                    </div>

                    <div style={styles.bookingSummaryFacts}>
                      <span>{formatMoney(item.grossAmountPence, item.currency)}</span>
                      <span>•</span>
                      <span>{item.dogCount ? `${item.dogCount} dog${item.dogCount === 1 ? "" : "s"}` : "Dogs not set"}</span>
                      {item.locationSummary ? (
                        <>
                          <span>•</span>
                          <span>{item.locationSummary}</span>
                        </>
                      ) : null}
                    </div>
                  </div>

                  <div style={styles.bookingSummarySide}>
                    <div style={styles.expandHint}>
                      {isExpanded ? "Hide details" : "Show details"}
                    </div>
                  </div>
                </button>

                {isExpanded ? (
                  <div style={styles.bookingDetailsArea}>
                    <div style={styles.detailGrid}>
                      <DetailItem
                        label="Service date"
                        value={formatDate(item.serviceDateLocal)}
                      />
                      <DetailItem
                        label="Time"
                        value={formatTimeRange(
                          item.startTimeLocal,
                          item.endTimeLocal
                        )}
                      />
                      <DetailItem
                        label="Dogs"
                        value={item.dogCount ? String(item.dogCount) : "—"}
                      />
                      <DetailItem
                        label="Amount"
                        value={formatMoney(item.grossAmountPence, item.currency)}
                      />
                      <DetailItem
                        label="Location"
                        value={item.locationSummary || "—"}
                      />
                      <DetailItem
                        label="Created"
                        value={formatDateTime(item.createdUtc)}
                      />
                    </div>

                    {item.notes ? (
                      <div style={styles.notesBox}>{item.notes}</div>
                    ) : null}

                    <div style={styles.cardFooter}>
                      <div style={styles.footerMeta}>
                        Proposal #{item.bookingProposalId}
                      </div>

                      {!readOnly && (
                        <div style={styles.rowActions}>
                          {canComplete && (
                            <button
                              style={styles.smallPrimaryButton}
                              onClick={() => onComplete(item.bookingProposalId)}
                              disabled={isBusy}
                            >
                              {isBusy ? "Saving..." : "Mark completed"}
                            </button>
                          )}

                          {canCancel && (
                            <button
                              style={styles.smallSecondaryButton}
                              onClick={() => onCancel(item.bookingProposalId)}
                              disabled={isBusy}
                            >
                              {isBusy ? "Saving..." : "Cancel"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

type DetailItemProps = {
  label: string;
  value: string;
};

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => {
  return (
    <div style={styles.detailItem}>
      <div style={styles.detailLabel}>{label}</div>
      <div style={styles.detailValue}>{value}</div>
    </div>
  );
};

function formatStatus(value?: string | null): string {
  const text = (value || "").trim();
  if (!text) return "Unknown";

  if (text.toLowerCase() === "checkoutcreated") {
    return "Checkout created";
  }

  return text;
}

function getStatusBadgeStyle(status?: string | null): React.CSSProperties {
  switch ((status || "").trim().toLowerCase()) {
    case "paid":
      return {
        background: "rgba(21, 128, 61, 0.12)",
        border: "1px solid rgba(21, 128, 61, 0.22)",
        color: "#166534",
      };
    case "completed":
      return {
        background: "rgba(37, 99, 235, 0.12)",
        border: "1px solid rgba(37, 99, 235, 0.22)",
        color: "#1d4ed8",
      };
    case "cancelled":
      return {
        background: "rgba(220, 38, 38, 0.12)",
        border: "1px solid rgba(220, 38, 38, 0.22)",
        color: "#b91c1c",
      };
    case "checkoutcreated":
      return {
        background: "rgba(180, 83, 9, 0.12)",
        border: "1px solid rgba(180, 83, 9, 0.22)",
        color: "#b45309",
      };
    case "sent":
      return {
        background: "rgba(75, 85, 99, 0.12)",
        border: "1px solid rgba(75, 85, 99, 0.22)",
        color: "#374151",
      };
    default:
      return {
        background: "rgba(47,79,70,0.08)",
        border: "1px solid rgba(47,79,70,0.14)",
        color: "#213a33",
      };
  }
}

function formatDate(value?: string | null): string {
  if (!value) return "—";

  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value?: string | null): string {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTimeRange(
  startTime?: string | null,
  endTime?: string | null
): string {
  if (!startTime && !endTime) return "—";
  if (startTime && endTime)
    return `${formatTime(startTime)} – ${formatTime(endTime)}`;
  if (startTime) return formatTime(startTime);
  return endTime ? formatTime(endTime) : "—";
}

function formatTime(value: string): string {
  return value.slice(0, 5);
}

function formatMoney(
  amountPence?: number | null,
  currency?: string | null
): string {
  const pence = typeof amountPence === "number" ? amountPence : 0;
  const code = (currency || "gbp").toUpperCase();

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: code,
  }).format(pence / 100);
}

function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242,138,22,0.16), transparent 28%), linear-gradient(180deg,#2f4f46 0%,#213a33 100%)",
    padding: "20px 12px 40px",
    color: "#fff",
  },

  container: {
    maxWidth: 920,
    margin: "0 auto",
  },

  heroCard: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 24,
    padding: 20,
    boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
  },

  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },

  topActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  badge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(47,79,70,0.08)",
    fontWeight: 800,
    marginBottom: 12,
    fontSize: 14,
  },

  title: {
    margin: 0,
    fontSize: 36,
    lineHeight: 1.1,
    fontWeight: 900,
    color: "#213a33",
  },

  textMuted: {
    margin: "10px 0 0",
    fontSize: 16,
    color: "rgba(24, 32, 29, 0.72)",
    lineHeight: 1.6,
    maxWidth: 680,
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
    marginTop: 22,
  },

  summaryCard: {
    borderRadius: 18,
    padding: 16,
    background: "rgba(33, 58, 51, 0.04)",
    border: "1px solid rgba(33, 58, 51, 0.08)",
  },

  summaryLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: "rgba(24, 32, 29, 0.72)",
  },

  summaryValue: {
    fontSize: 30,
    fontWeight: 900,
    color: "#213a33",
    marginTop: 8,
  },

  section: {
    marginTop: 20,
  },

  sectionToggle: {
    width: "100%",
    textAlign: "left",
    border: "1px solid rgba(33, 58, 51, 0.08)",
    borderRadius: 20,
    padding: 16,
    background: "rgba(33, 58, 51, 0.04)",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },

  sectionToggleLeft: {
    minWidth: 0,
    flex: 1,
  },

  sectionToggleRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },

  sectionTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 900,
    color: "#213a33",
  },

  sectionPreview: {
    marginTop: 6,
    fontSize: 14,
    color: "rgba(24, 32, 29, 0.68)",
    lineHeight: 1.5,
  },

  sectionCount: {
    minWidth: 36,
    height: 36,
    padding: "0 12px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    background: "rgba(33, 58, 51, 0.08)",
    color: "#213a33",
  },

  chevron: {
    width: 36,
    height: 36,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    color: "#213a33",
    fontWeight: 900,
    fontSize: 22,
    border: "1px solid rgba(33, 58, 51, 0.08)",
  },

  emptyState: {
    marginTop: 28,
    borderRadius: 22,
    padding: 24,
    background: "rgba(33, 58, 51, 0.04)",
    border: "1px solid rgba(33, 58, 51, 0.08)",
  },

  emptyRow: {
    marginTop: 12,
    borderRadius: 18,
    padding: 18,
    background: "rgba(33, 58, 51, 0.03)",
    border: "1px dashed rgba(33, 58, 51, 0.14)",
    color: "rgba(24, 32, 29, 0.72)",
  },

  list: {
    display: "grid",
    gap: 12,
    marginTop: 12,
  },

  bookingCard: {
    borderRadius: 20,
    background: "#fff",
    border: "1px solid rgba(33, 58, 51, 0.08)",
    boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },

  bookingSummaryButton: {
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "#fff",
    cursor: "pointer",
    padding: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },

  bookingSummaryMain: {
    minWidth: 0,
    flex: 1,
  },

  bookingSummarySide: {
    flexShrink: 0,
    paddingTop: 4,
  },

  bookingTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  },

  bookingTitle: {
    fontSize: 22,
    fontWeight: 900,
    color: "#213a33",
    lineHeight: 1.2,
  },

  bookingSummaryLine: {
    marginTop: 8,
    fontSize: 16,
    fontWeight: 700,
    color: "#213a33",
    lineHeight: 1.5,
  },

  bookingSummaryLineSoft: {
    marginTop: 4,
    fontSize: 15,
    color: "rgba(24, 32, 29, 0.72)",
    lineHeight: 1.5,
  },

  bookingSummaryFacts: {
    marginTop: 10,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
    fontSize: 14,
    color: "rgba(24, 32, 29, 0.66)",
    fontWeight: 700,
    lineHeight: 1.5,
  },

  expandHint: {
    fontSize: 14,
    fontWeight: 800,
    color: "#213a33",
    padding: "8px 10px",
    borderRadius: 999,
    background: "rgba(33, 58, 51, 0.06)",
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 12px",
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 13,
  },

  bookingDetailsArea: {
    padding: "0 16px 16px",
    borderTop: "1px solid rgba(33, 58, 51, 0.06)",
  },

  detailGrid: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 10,
  },

  detailItem: {
    padding: 12,
    borderRadius: 14,
    background: "rgba(33, 58, 51, 0.04)",
    border: "1px solid rgba(33, 58, 51, 0.06)",
  },

  detailLabel: {
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "rgba(24, 32, 29, 0.55)",
  },

  detailValue: {
    marginTop: 6,
    fontSize: 15,
    fontWeight: 700,
    color: "#213a33",
    lineHeight: 1.4,
  },

  notesBox: {
    marginTop: 14,
    padding: 14,
    borderRadius: 14,
    background: "rgba(242, 138, 22, 0.06)",
    border: "1px solid rgba(242, 138, 22, 0.12)",
    color: "#6b3f09",
    lineHeight: 1.6,
    fontSize: 15,
  },

  cardFooter: {
    marginTop: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  footerMeta: {
    fontSize: 13,
    color: "rgba(24, 32, 29, 0.62)",
    fontWeight: 700,
  },

  rowActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  actions: {
    marginTop: 18,
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },

  primaryButton: {
    padding: "12px 18px",
    borderRadius: 16,
    border: "none",
    background: "#f28a16",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 16,
  },

  secondaryButton: {
    padding: "12px 18px",
    borderRadius: 16,
    border: "none",
    background: "#213a33",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 16,
  },

  smallPrimaryButton: {
    padding: "10px 14px",
    borderRadius: 14,
    border: "none",
    background: "#f28a16",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 15,
  },

  smallSecondaryButton: {
    padding: "10px 14px",
    borderRadius: 14,
    border: "none",
    background: "#213a33",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 15,
  },

  errorBox: {
    marginTop: 18,
    borderRadius: 16,
    padding: 14,
    background: "rgba(220, 38, 38, 0.08)",
    border: "1px solid rgba(220, 38, 38, 0.14)",
    color: "#7f1d1d",
    fontWeight: 700,
  },
};

export default WalkerBookingsPage;